import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApproveForPayment, useInvoiceList } from '../../api/invoices';
import { useCurrentUser } from '../../auth/auth-provider';
import { Button } from '../../components/ui/button';
import { EyeIcon } from '../../components/ui/icons';
import { InvoiceFlags, hasInvoiceFlags } from '../../components/ui/status-pill';
import { EmptyState, ErrorBox, LoadingBlock } from '../../components/ui/feedback';
import { ApiError } from '../../lib/api-client';
import { formatAmount, formatMoney, formatShortDate } from '../../lib/format';
import { managerLabel, projectLabel } from '../invoices/invoice-labels';
import styles from '../../components/layout/page.module.css';

/**
 * The owner's one job, done in bulk: tick, approve, done.
 *
 * An invoice still missing its handover protocol is flagged but selectable —
 * the block was lifted on the client's decision of 3.09.2026. The flag is the
 * point: the owner decides with the fact in front of them, rather than being
 * stopped by a rule and left to chase the protocol before paying anything.
 */
export function ApprovalsPage() {
  const navigate = useNavigate();
  const user = useCurrentUser();
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const approve = useApproveForPayment();

  const { data, isLoading, error } = useInvoiceList({
    status: 'APPROVED_BY_MANAGER',
    sortBy: 'dueDate',
    sortDir: 'asc',
    pageSize: 100,
  });

  if (isLoading) return <LoadingBlock />;
  if (error) return <ErrorBox>Nie udało się pobrać listy.</ErrorBox>;

  const invoices = data?.items ?? [];
  const selectedInvoices = invoices.filter((invoice) => selected.has(invoice.id));

  function toggle(invoiceId: string) {
    setSelected((current) => {
      const next = new Set(current);
      if (next.has(invoiceId)) next.delete(invoiceId);
      else next.add(invoiceId);
      return next;
    });
  }

  function toggleAll() {
    setSelected((current) =>
      current.size === invoices.length ? new Set() : new Set(invoices.map((i) => i.id)),
    );
  }

  async function handleApprove() {
    await approve.mutateAsync({ invoiceIds: [...selected] });
    setSelected(new Set());
  }

  return (
    <>
      <header className={styles.header}>
        <h1 className={styles.title}>Do zatwierdzenia</h1>
        <span className={styles.subtitle}>
          {data && data.total > invoices.length
            ? `${invoices.length} z ${data.total} faktur — pokazane najpilniejsze`
            : `${invoices.length} ${invoices.length === 1 ? 'faktura' : 'faktur'}`}{' '}
          · {formatMoney(sum(invoices.map((i) => i.amountDue)))} · zaakceptowane przez kierowników
        </span>
      </header>

      {user.role === 'ACCOUNTANT' && (
        <div className={`${styles.banner} ${styles.bannerInfo}`}>
          <span aria-hidden="true">ⓘ</span>
          <span>
            Zatwierdzasz bez akceptacji właściciela. Każda faktura zatwierdzona tutaj dostaje trwały
            znacznik „Bez akceptacji właściciela”, widoczny w rejestrze, rozrachunkach i na pulpicie.
          </span>
        </div>
      )}

      {approve.error && (
        <ErrorBox>
          {approve.error instanceof ApiError
            ? approve.error.message
            : 'Nie udało się zatwierdzić faktur.'}
        </ErrorBox>
      )}

      {selected.size > 0 && (
        <div className={styles.selectionBar}>
          <span aria-hidden="true">✓</span>
          <span>
            Zaznaczono {selected.size} · {formatMoney(sum(selectedInvoices.map((i) => i.amountDue)))}
          </span>
          <span className={styles.selectionAction}>
            <Button isLoading={approve.isPending} onClick={() => void handleApprove()}>
              Zatwierdź do zapłaty
            </Button>
          </span>
        </div>
      )}

      {invoices.length === 0 ? (
        <EmptyState title="Nic nie czeka" description="Wszystkie faktury są zatwierdzone." />
      ) : (
        <div className={styles.tableWrap}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th style={{ width: 36 }}>
                  <input
                    type="checkbox"
                    className={styles.checkbox}
                    aria-label="Zaznacz wszystkie"
                    checked={invoices.length > 0 && selected.size === invoices.length}
                    onChange={toggleAll}
                  />
                </th>
                <th>Kontrahent</th>
                <th>Budowa</th>
                <th>Zaakceptował</th>
                <th>Termin</th>
                <th className={styles.numeric}>Do zapłaty</th>
                <th>Uwagi</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {invoices.map((invoice) => (
                <tr key={invoice.id}>
                  <td>
                    <input
                      type="checkbox"
                      className={styles.checkbox}
                      aria-label={`Zaznacz fakturę ${invoice.number}`}
                      checked={selected.has(invoice.id)}
                      onChange={() => toggle(invoice.id)}
                    />
                  </td>
                  <td className={styles.strong}>{invoice.vendorName}</td>
                  <td className={projectLabel(invoice).isPlaceholder ? styles.muted : undefined}>
                    {projectLabel(invoice).text}
                  </td>
                  <td className={styles.muted}>{managerLabel(invoice)}</td>
                  <td>{formatShortDate(invoice.dueDate)}</td>
                  <td className={`${styles.numeric} ${styles.strong}`}>
                    {formatAmount(invoice.amountDue)}
                  </td>
                  <td>
                    {hasInvoiceFlags(invoice) ? (
                      <span className={styles.flagCell}>
                        <InvoiceFlags invoice={invoice} />
                      </span>
                    ) : (
                      <span className={styles.muted}>—</span>
                    )}
                  </td>
                  <td>
                    <div className={styles.rowActions}>
                      <Button
                        variant="soft"
                        icon={<EyeIcon />}
                        onClick={() => void navigate(`/faktury/${invoice.id}`)}
                      >
                        Podgląd
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}


function sum(amounts: number[]): number {
  return amounts.reduce((total, amount) => total + Math.round(amount * 100), 0) / 100;
}
